"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  OakFlex,
  OakHeading,
  OakHoverLink,
  OakLI,
  OakPupilJourneyYearButton,
} from "@oaknational/oak-components";

import type { FactorData, Programme } from "@/types/ui";

type Props<programme = Programme> = {
  yearSlug?: string;
  programmes: Programme[];
  baseSlug: string;
  programmeUrlTemplate: string;
  backUrlTemplate: string;
  getAvailableProgrammeFactorAction: (args: {
    factorPrefix: "tier" | "examboard" | "pathway";
    programmes: programme[];
  }) => Promise<FactorData[]>;
  onOptionSelected?: (
    factorType: "tier" | "examboard" | "pathway",
    factor: FactorData,
  ) => void;
};

export const OptionsView = ({
  yearSlug,
  programmes,
  baseSlug,
  programmeUrlTemplate,
  backUrlTemplate,
  getAvailableProgrammeFactorAction,
  onOptionSelected,
}: Props<never>) => {
  const [pathways, setPathways] = useState<FactorData[]>([]);
  const [tiers, setTiers] = useState<FactorData[]>([]);
  const [examboards, setExamboards] = useState<FactorData[]>([]);
  const [chosenPathway, setChosenPathway] = useState<FactorData | undefined>();
  const [chosenExamboard, setChosenExamboard] = useState<
    FactorData | undefined
  >();
  const [chosenTier, setChosenTier] = useState<FactorData | undefined>();
  const router = useRouter();

  const getOptionsAvailable = (
    factor: FactorData,
    factorType: "pathway" | "tier" | "examboard",
  ) => {
    const programmesWithFactor = programmes.filter(
      (p) =>
        p.programmeFields[`${factorType}Slug`] === factor.factorSlug &&
        (p.programmeFields.legacy === "true") === factor.isLegacy,
    );
    return programmesWithFactor.length > 1;
  };

  const getUrl = (potentialFactor?: Programme | FactorData) => {
    const allFactors = [
      chosenPathway,
      chosenTier,
      chosenExamboard,
      potentialFactor,
    ].filter((f) => !!f);
    const optionalSlug = allFactors
      .map((f) => (f as FactorData)?.factorSlug)
      .join("-");
    const isLegacy = allFactors.some((f) => (f as FactorData)?.isLegacy);
    const slug = `${baseSlug}-${optionalSlug}${isLegacy ? "-l" : ""}`;
    return programmeUrlTemplate.replace(":programmeSlug", slug);
  };

  if (!programmes || !programmes.length) {
    // todo: handle properly
    throw new Error("No programmes found.");
  }
  const nonLegacyUnits = programmes.filter(
    (p) => (p as Programme)?.programmeFields.legacy !== "true",
  );
  const hasOneNonLegacyUnit = nonLegacyUnits.length === 1;
  if (hasOneNonLegacyUnit) {
    router.push(getUrl(nonLegacyUnits[0] as unknown as Programme | FactorData));
  }

  const loadAvailableFactors = React.useCallback(async () => {
    const pathwaysRes = await getAvailableProgrammeFactorAction({
      factorPrefix: "pathway",
      programmes,
    } as never);
    const tiersRes = await getAvailableProgrammeFactorAction({
      factorPrefix: "tier",
      programmes,
    } as never);
    const examboardsRes = await getAvailableProgrammeFactorAction({
      factorPrefix: "examboard",
      programmes,
    } as never);
    setPathways(pathwaysRes);
    setTiers(tiersRes);
    setExamboards(examboardsRes);
  }, [getAvailableProgrammeFactorAction, programmes]);

  const getOptions = () => {
    type ChosenOption = {
      title: string;
      factorType: "pathway" | "tier" | "examboard";
      values: FactorData[];
      onSelect: (v: FactorData) => void;
      chosen: FactorData | undefined;
      required: boolean;
    };

    const allFactors: ChosenOption[] = [
      {
        required: pathways.length > 0,
        chosen: chosenPathway,
        title: "a pathway",
        factorType: "pathway",
        values: pathways,
        onSelect: setChosenPathway,
      },
      {
        required: tiers.length > 0,
        chosen: chosenTier,
        title: "a tier",
        factorType: "tier",
        values: tiers,
        onSelect: setChosenTier,
      },
      {
        // No explicit core check needed - getOptionsAvailable handles this
        required: examboards.length > 0,
        chosen: chosenExamboard,
        title: "an examboard",
        factorType: "examboard",
        values: examboards,
        onSelect: setChosenExamboard,
      },
    ];

    const option = allFactors.find((f) => f.required && !f.chosen);
    if (!option) return;
    const unchosenCount = allFactors.filter(
      (f) => f.required && !f.chosen,
    ).length;
    const isLastOption = unchosenCount === 1;

    return (
      <OakFlex
        $ph={"spacing-24"}
        $flexDirection={"column"}
        $alignItems={"center"}
      >
        <OakHeading tag="h1" $font={"heading-7"} $mv={"spacing-24"}>
          Choose {option.title}
        </OakHeading>
        <OakFlex
          as="ul"
          $ma={"spacing-0"}
          $pa={"spacing-0"}
          $gap={"spacing-16"}
          $flexWrap={"wrap"}
          $justifyContent={"space-evenly"}
        >
          {option.values.map((factor) => {
            const hasMoreOptions = getOptionsAvailable(
              factor,
              option.factorType,
            );
            const shouldNavigate = isLastOption || !hasMoreOptions;
            return (
              <OakLI $listStyle="none" key={factor.factorSlug}>
                <OakPupilJourneyYearButton
                  role={shouldNavigate ? "link" : "button"}
                  element={shouldNavigate ? Link : "button"}
                  href={shouldNavigate ? getUrl(factor) : undefined}
                  phase={"secondary"}
                  onClick={
                    !shouldNavigate
                      ? () => {
                          option?.onSelect(factor);
                          onOptionSelected?.(option.factorType, factor);
                        }
                      : () => {
                          onOptionSelected?.(option.factorType, factor);
                        }
                  }
                >
                  {option?.title === "a tier"
                    ? factor.factorDescription
                    : factor.factor}
                </OakPupilJourneyYearButton>
              </OakLI>
            );
          })}
        </OakFlex>
      </OakFlex>
    );
  };

  React.useEffect(() => {
    loadAvailableFactors();
  }, [loadAvailableFactors]);

  return (
    <OakFlex
      $flexDirection="column"
      $background={"mint"}
      $alignItems={"center"}
      $justifyContent={"flex-start"}
      $minHeight={"100vh"}
    >
      <OakFlex
        $alignSelf={"flex-start"}
        $mt={"spacing-32"}
        $ml={"spacing-56"}
        $mb={"spacing-16"}
      >
        <OakHoverLink
          href={backUrlTemplate.replace(":yearSlug", yearSlug ?? "")}
          iconName="chevron-left"
          element={Link}
        >
          Back
        </OakHoverLink>
      </OakFlex>
      <OakFlex $flexDirection="column" $mb={"spacing-32"}>
        <OakFlex
          $background={"bg-primary"}
          $pv={["spacing-16", "spacing-32", "spacing-48"]}
          $ph={["spacing-16", "spacing-16", "spacing-24"]}
          $flexDirection={"column"}
          $alignItems={"center"}
          $justifyContent={"center"}
          $borderRadius={"border-radius-l"}
          $ba={"border-solid-s"}
          $borderColor={"border-decorative1-stronger"}
          $minWidth={"spacing-960"}
          $minHeight={"spacing-360"}
        >
          {getOptions()}
        </OakFlex>
      </OakFlex>
    </OakFlex>
  );
};
